import { ImageHandlingHeaderMobileProps } from '@v2/components/imageHandling/components/Header/HeaderMobile';
import { useSelector } from 'react-redux';
import { isImageContextHasScopeId, selectPreImageHandlingScreen } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { ImageUpload, isWeb, navigateTo, navigationRef } from '@helpers';
import { Element, scale as initialScale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types.d';
import { Text, View } from 'react-native';
import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import i18n from 'i18n-js';

interface UseManageImageHandlingHeaderMobile
  extends Pick<
    ImageHandlingHeaderMobileProps,
    'images' | 'selectedIndex' | 'imageContextForAdding' | 'isSingleImage' | 'setMode' | 'scale' | 'font' | 'showAddImageModal' | 'C'
  > {
    onBackButtonPress: () => void;
  }

export const useManageImageHandlingHeaderMobile = ({
  images,
  selectedIndex,
  imageContextForAdding,
  isSingleImage,
  setMode,
  showAddImageModal,
  C,
  onBackButtonPress,
  scale = initialScale,
  font = initialFont,
}: UseManageImageHandlingHeaderMobile) => {
  const img = images[selectedIndex] ?? {};

  const route = navigationRef.current?.getCurrentRoute();
  const preImageHandlingScreen = useSelector(selectPreImageHandlingScreen);

  const params = route.params;

  const onBackPressed = () => {
    if(onBackButtonPress) {
      onBackButtonPress();
      return;
    }
    if (!navigationRef.current.canGoBack() || !preImageHandlingScreen) {
      navigateTo(isWeb ? 'Machines' : 'Authorized');
      return;
    }

    navigationRef.current.navigate(preImageHandlingScreen);
  };

  const isStartThumbnail = params && params?.imageHandlingMode && params?.imageHandlingMode === 'thumbnail';

  const onAddImagePress = () => {
    ImageUpload({ saveToMainStore: true, imageContextForAdding });
  };

  const BackComponent = () => (
    <Element
      key={'BackComponent'}
      scale={scale}
      {...{
        config: {
          position: {
            top: 8,
            left: 8,
          },
          height: 55,
          zIndex: 10000,
        },
      }}
    >
      <TouchableElement onPress={isStartThumbnail && setMode ? () => setMode(ImageHandlingDisplayMode.THUMBNAIL) : onBackPressed}>
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                width: '100%',
                height: '100%',
                alignItems: 'center',
                flexDirection: 'row',
              },
            ]}
          >
            <Svg icon="chevronLeft" width={scale(10)} height={scale(17)} />
            <Text style={[font.Body, { color: C('Link / Light / Primary'), paddingLeft: scale(8) }]}>{i18n.t('Back')}</Text>
          </View>
        )}
      </TouchableElement>
    </Element>
  );

  const AddComponent = () => (
    <Element
      key={'AddComponent'}
      scale={scale}
      {...{
        config: {
          position: {
            top: 13,
            right: 6,
          },
          height: 55,
          zIndex: 10000,
        },
      }}
    >
      <TouchableElement onPress={onAddImagePress}>
        {({ boxStyle, pressHoverColor }) => (
          <View style={{ width: scale(40), height: scale(55) }}>
            <View
              style={[
                boxStyle,
                {
                  width: '100%',
                  height: '100%',
                  marginTop: scale(-5),
                  justifyContent: 'center',
                  alignItems: 'center',
                },
              ]}
            >
              <Svg icon="plus" width={scale(16)} height={scale(16)} color={showAddImageModal ? 'Button/Light/Secondary' : 'Button/Light/Primary'} />
            </View>
          </View>
        )}
      </TouchableElement>
    </Element>
  );

  const actions = [{ component: BackComponent, key: 'BackComponent' }];
  if (!isSingleImage) {
    actions.push({ component: AddComponent, key: 'AddComponent' });
  }

  return { img, actions };
};
